import { breakpoints, Inline, Stack, Text, vars } from '@etg/wings';
import type { CurrencyAmount } from '@eti/schema-types';
import { css } from '@eti/styles';
import { useProperty, useTranslation } from '@eti/providers';
import { getMobileDatesPrices } from '../utils/getMobileDatesPrices';
import NearbyDatePrice from './NearbyDatePrice';

const nearbyDatesPricesStyles = (isLabelAtBottom: boolean) => css`
  border: 1px solid ${vars.colors.divider};
  border-radius: ${vars.button.borderRadius};
  width: 100%;
  ${!isLabelAtBottom && 'margin-bottom: 16px;'}
`;

const nearbyDatesPricesLabelStyles = (isLabelAtBottom: boolean) => css`
  color: ${vars.colors.promotional.dark};
  ${isLabelAtBottom ? 'margin: 16px 0;' : 'margin-bottom: 16px;'}
`;

export const NearbyPricesWithoutSelectedTripVersionEnumTypes = {
  A: 'A',
  B: 'B',
  C: 'C',
} as const;
export type NearbyPricesWithoutSelectedTripVersionEnumTypesValues =
  (typeof NearbyPricesWithoutSelectedTripVersionEnumTypes)[keyof typeof NearbyPricesWithoutSelectedTripVersionEnumTypes];

export const NearbyPricesWithSelectedTripVersionEnumTypes = {
  A: 'A',
  B: 'B',
} as const;
export type NearbyPricesWithSelectedTripVersionEnumTypesValues =
  (typeof NearbyPricesWithSelectedTripVersionEnumTypes)[keyof typeof NearbyPricesWithSelectedTripVersionEnumTypes];

export type NearbyDatePriceType = {
  departureDate: string;
  isCheapest: boolean;
  isSelected: boolean;
  price: CurrencyAmount;
  returnDate?: string;
};

export type NearbyDatesPricesProps = {
  data?: NearbyDatePriceType[];
  handleSearch: (keepFilters: boolean) => void;
  hasSelectedTrip: boolean;
};

// TODO: Remove as soon as we have backend implementation DSO-804
const datesPricesMock: NearbyDatePriceType[] = [
  {
    departureDate: '2025-03-01T20:31:13.913Z',
    isCheapest: false,
    isSelected: false,
    price: {
      value: 1111,
      currency: {
        code: 'SEK',
        exponent: 0,
        name: '',
        __typename: 'Currency',
      },
      __typename: 'CurrencyAmount',
    },
    returnDate: '2025-03-02T00:31:13.913Z',
  },
  {
    departureDate: '2025-03-02T20:31:13.913Z',
    isCheapest: false,
    isSelected: false,
    price: {
      value: 2222,
      currency: {
        code: 'SEK',
        exponent: 0,
        name: '',
        __typename: 'Currency',
      },
      __typename: 'CurrencyAmount',
    },
    returnDate: '2025-03-03T00:31:13.913Z',
  },
  {
    departureDate: '2025-03-03T20:31:13.913Z',
    isCheapest: false,
    isSelected: true,
    price: {
      value: 3333,
      currency: {
        code: 'SEK',
        exponent: 0,
        name: '',
        __typename: 'Currency',
      },
      __typename: 'CurrencyAmount',
    },
    returnDate: '2025-03-04T00:31:13.913Z',
  },
  {
    departureDate: '2025-03-04T20:31:13.913Z',
    isCheapest: true,
    isSelected: false,
    price: {
      value: 999,
      currency: {
        code: 'SEK',
        exponent: 0,
        name: '',
        __typename: 'Currency',
      },
      __typename: 'CurrencyAmount',
    },
    returnDate: '2025-03-05T00:31:13.913Z',
  },
  {
    departureDate: '2025-03-05T20:31:13.913Z',
    isCheapest: false,
    isSelected: false,
    price: {
      value: 5555,
      currency: {
        code: 'SEK',
        exponent: 0,
        name: '',
        __typename: 'Currency',
      },
      __typename: 'CurrencyAmount',
    },
    returnDate: '2025-03-06T00:31:13.913Z',
  },
];

const NearbyDatesPrices = ({
  data = datesPricesMock,
  hasSelectedTrip,
  handleSearch,
}: NearbyDatesPricesProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();

  const nearbyDatesPricesLabel = t('Result.NearbyPrices.Label');

  const NearbyPricesWithoutSelectedTripVersion: (typeof NearbyPricesWithoutSelectedTripVersionEnumTypes)[keyof typeof NearbyPricesWithoutSelectedTripVersionEnumTypes] =
    p('Result.Page.NearbyPrices.WithoutSelectedTrip.Version');

  const renderLabelAtBottom =
    !hasSelectedTrip &&
    (NearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.A ||
      NearbyPricesWithoutSelectedTripVersion === NearbyPricesWithoutSelectedTripVersionEnumTypes.B);

  const getDatesPrices = () => {
    const isMobileDevice = window.matchMedia(`(max-width: ${breakpoints._768})`).matches;

    return isMobileDevice ? getMobileDatesPrices(data) : data;
  };

  return (
    <Stack data-testid="nearby-dates-prices" spacing={0}>
      {!renderLabelAtBottom && (
        <Text
          as="div"
          className={nearbyDatesPricesLabelStyles(renderLabelAtBottom)}
          data-testid="nearby-dates-prices-label-at-top"
          variant="14-bold"
        >
          {nearbyDatesPricesLabel}
        </Text>
      )}
      <Inline className={nearbyDatesPricesStyles(renderLabelAtBottom)} noWrap spacing={0}>
        {getDatesPrices().map((datePrice, index) => (
          <NearbyDatePrice
            departureDate={datePrice.departureDate}
            handleSearch={handleSearch}
            isCheapest={datePrice.isCheapest}
            isSelected={datePrice.isSelected}
            key={index}
            price={datePrice.price}
            returnDate={datePrice.returnDate}
          />
        ))}
      </Inline>
      {renderLabelAtBottom && (
        <Text
          as="div"
          className={nearbyDatesPricesLabelStyles(renderLabelAtBottom)}
          data-testid="nearby-dates-prices-label-at-bottom"
          variant="14-bold"
        >
          {nearbyDatesPricesLabel}
        </Text>
      )}
    </Stack>
  );
};
export default NearbyDatesPrices;
